import Vue from 'vue'

export function FavoriteStorage(api, chatApi, cacheActive=true) {
    let self = this
    self.user = {}
    self.websocket = null
    self.favorite_keys = ['favorite_session', 'favorite_speaker', 'favorite_exhibitor',
                          'favorite_sponsor']

    self.observed = Vue.observable({
      numDirectUnreadMessages: 0,
      contactIds: [],
      directChannels: [],
      // bumped on every incoming direct message so list views can refetch
      // (server sorts contacts by has_unread / last_post_at)
      directMessageCounter: 0,
      currentEventShortName: localStorage.getItem('currentEventShortName') || ''
    })

    self.setObserved = function(key, value) {
      if (Object.prototype.hasOwnProperty.call(self.observed, key))
        self.observed[key] = value
      else
        throw(`unknown observable: ${key}`)
    }

    self.setCurrentEventShortName = function(shortName) {
      self.observed.currentEventShortName = shortName
      localStorage.setItem('currentEventShortName', shortName)
    }

    self.getToken = function() {
      return localStorage.getItem('speaker_tool_token')
    }

    self.getUserUuid = function() {
      return self.get('user_uuid')
    }

    self.get = function(key, Default=null) {
      let item = localStorage.getItem(key)
      return item ? item : Default
    }

    self.getItem = function(key, Default=null) {
      let item = localStorage.getItem(key)
      return item ? JSON.parse(item) : Default
    }

    self.setItem = function(key, value) {
      localStorage.setItem(key, JSON.stringify(value))
    }

    self.set = function(key, value) {
      localStorage.setItem(key, value)
    }

    self.getFavorites = function() {
      let favorites = {}
      for (let k of self.favorite_keys) {
        let values = localStorage.getItem(k)
        favorites[k] = values ? JSON.parse(values) : []
      }
      return favorites
    }

    self.fetchUser = function() {
      if (self.getToken() && navigator.onLine) {
          return api.get(`user-profile/${self.getToken()}/`).then((response) => {
              self.user = response.data
              self.initStorage()
              localStorage.setItem('speaker_tool_user', JSON.stringify(self.user))
              return response
          }).catch(error_response => {
            self.user = self.getItem('speaker_tool_user', {})
            console.error(error_response)
            throw error_response
          })
      }
      self.user = self.getItem('speaker_tool_user', {})
      self.initStorage()
      return Promise.resolve({data: self.user})
    }

    self.initStorage = function() {
      console.log('initStorage()')
      let must_update = false
      let remote_favorites = {}
      for (let k of self.favorite_keys) {
        let values = localStorage.getItem(k) ? JSON.parse(localStorage.getItem(k)) : null
        let remote_values = self.user?.customer?.favorites[k]
        if (values && !remote_values) {
          remote_favorites[k] = values
          must_update = true
        } else if (remote_values) {
          localStorage.setItem(k, JSON.stringify(remote_values))
          values = remote_values
        }
        if (cacheActive)
          self.cacheFavorites(k, values)
      }
      if (must_update && self.getToken() && navigator.onLine)
        self.updateRemoteFavorites(remote_favorites)
    }

    self.cacheFavorites = function(key, values) {
      // caches (overwrites) the local storage favorite objs for offline usage
      key = key.slice(9) + 's'
      if (!values || !values.length) {
        localStorage.removeItem(key)
        return
      }

      let pk = ['sessions', 'speakers'].indexOf(key) >= 0 ? 'content_code' : 'sfid'
      let params = {}
      let payload = {}
      payload[pk] = values
      let url = key === 'sessions' ? '/session-index/' : `/${key}/`
      api.post(url, payload, {params: params}).then(response => {
        let objs = {}
        for (let obj of response.data.results) {
            objs[obj[pk]] = obj
        }
        localStorage.setItem(key, JSON.stringify(objs))
      })
    }

    self.updateRemoteFavorites = function(favorites) {
      let customer = {favorites: favorites}
      api.patch(`user-profile/${self.getToken()}/`, {customer: customer}).then((response) => {
        self.user = response.data
      })
    }

    self.updateRemoteFavoriteKey = function(key, values) {
      let favorites = {}
      for (let k of self.favorite_keys) {
        let vals = localStorage.getItem(k) ? JSON.parse(localStorage.getItem(k)) : null
        if (vals)
            favorites[k] = vals
      }
      favorites[key] = values

      api.patch(`user-profile/${self.getToken()}/`, {customer: {favorites: favorites}}).then((response) => {
        self.user = response.data
      })
    }

    self.fetchContactsForUser = function() {
      console.log('fetchContactsForUser')

      return chatApi.get('channels/').then(response => {
        let channels = response.data.results || response.data
        let directChannels = channels.filter(channel => channel.channel_type == 'D') || []

        // Set numUnreadMessages BEFORE assigning to observable,
        // so Vue 2 makes the property reactive from the start.
        self.observed.numDirectUnreadMessages = 0
        for (const c of directChannels) {
          c.numUnreadMessages = c.unread_count || 0
          self.observed.numDirectUnreadMessages += c.numUnreadMessages
        }

        self.observed.directChannels = directChannels
        self.setItem('direct-channels', self.observed.directChannels)

        let userUuid = self.getUserUuid()
        self.observed.contactIds = self.observed.directChannels.map(channel => {
          // channel.name = "uuid_a__uuid_b" - extract the other uuid
          let parts = channel.name.split('__')
          return parts[0] === userUuid ? parts[1] : parts[0]
        })
        self.setItem('contact-ids', self.observed.contactIds)
        return {data: {directChannels: self.observed.directChannels, contactIds: self.observed.contactIds}}
      })
    }

    self.getDirectChannel = function(contactId) {
      return self.observed.directChannels.filter(channel => channel.name.includes(contactId))[0]
    }

    self._lastUnreadsFetch = 0

    self._isValidChannelCache = function(channels) {
      // Reject empty or non-array caches and stale Mattermost-format IDs
      // (26-char alphanumeric without dashes vs UUID with dashes)
      if (!Array.isArray(channels) || !channels.length) return false
      return channels.every(c => c && c.id && String(c.id).includes('-'))
    }

    self.getDirectUnreads = function() {
      // fetch all contacts (private message channels) of current user and their unread msg.
      let now = Date.now()
      if (now - self._lastUnreadsFetch < 5000) return Promise.resolve()
      self._lastUnreadsFetch = now
      let userUuid = self.getUserUuid()
      if (userUuid && navigator.onLine) {
        return self.fetchContactsForUser()
      } else if (userUuid) {
        let cached = self.getItem('direct-channels', [])
        if (self._isValidChannelCache(cached)) {
          self.observed.directChannels = cached
          self.observed.contactIds = self.getItem('contact-ids', [])
          // Recalculate unreads from cached data
          self.observed.numDirectUnreadMessages = 0
          for (const c of cached) {
            self.observed.numDirectUnreadMessages += (c.numUnreadMessages || 0)
          }
        } else {
          // Stale or invalid cache — discard it
          localStorage.removeItem('direct-channels')
          localStorage.removeItem('contact-ids')
          self.observed.directChannels = []
          self.observed.contactIds = []
          self.observed.numDirectUnreadMessages = 0
        }
      } else {
        self.observed.directChannels = []
        self.observed.contactIds = []
        self.observed.numDirectUnreadMessages = 0
      }
      return Promise.resolve()
    }

    self.markChannelAsRead = function(channelId) {
      if (self.getToken()) {
        chatApi.post(`channels/${channelId}/view/`).then(() => {
          let channel = self.observed.directChannels.filter(channel => channel.id == channelId)[0]
          if (channel) {
            self.observed.numDirectUnreadMessages -= channel.numUnreadMessages
            channel.numUnreadMessages = 0
          }
        })
      }
    }

    // External 'message' listeners (e.g. open channel views). Kept here so
    // they survive a reconnect: a new WebSocket object gets them re-attached.
    self._messageListeners = []
    self._wsShouldReconnect = false
    self._wsReconnectAttempts = 0
    self._wsReconnectTimer = null
    self._wsHadConnection = false

    self.addMessageListener = function(fn) {
      if (!self._messageListeners.includes(fn))
        self._messageListeners.push(fn)
      if (self.websocket)
        self.websocket.addEventListener('message', fn)
    }

    self.removeMessageListener = function(fn) {
      self._messageListeners = self._messageListeners.filter(f => f !== fn)
      if (self.websocket)
        self.websocket.removeEventListener('message', fn)
    }

    self._scheduleWsReconnect = function() {
      // Schedule ONE delayed reconnect attempt after the socket died
      // unexpectedly. There is no explicit retry loop: if the attempt fails,
      // its onclose handler calls this function again - that chain IS the
      // loop. It ends as soon as a connection succeeds (onopen resets
      // _wsReconnectAttempts to 0) and no timer exists during normal
      // operation.
      //
      // _wsShouldReconnect is false after an intentional close (logout via
      // disConnectWebSocket) - without this guard the onclose of the closed
      // socket would immediately reconnect against the logout.
      if (!self._wsShouldReconnect || self._wsReconnectTimer) return
      // No token -> the consumer would reject us anyway.
      if (!self.getToken()) return

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, then capped at 30s.
      // First retry is fast (short blip, deploy restart), but a longer
      // outage does not get hammered by thousands of clients per second.
      let delay = Math.min(30000, 1000 * Math.pow(2, self._wsReconnectAttempts))
      self._wsReconnectAttempts += 1

      // Only one pending timer at a time (guarded above) - onclose and the
      // error path of connectWebSocket may both call this.
      self._wsReconnectTimer = setTimeout(() => {
        self._wsReconnectTimer = null
        if (navigator.onLine)
          self.connectWebSocket()
        else
          // Browser reports offline - an attempt would be pointless. Just
          // re-arm the timer as a fallback heartbeat (max every 30s); when
          // the network returns, the 'online' handler reconnects right away.
          self._scheduleWsReconnect()
      }, delay)
    }

    self.ensureWebSocket = function() {
      // Reconnect if the socket died (proxy idle timeout, standby, deploy).
      if (!self.getToken() || !navigator.onLine) return
      let ws = self.websocket
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))
        return
      self.connectWebSocket()
    }

    self.connectWebSocket = function() {
      let wsUrl = chatApi.defaults.webSocketUrl
      if (!wsUrl) return

      let token = self.getToken()
      if (!token) return

      self._wsShouldReconnect = true
      if (self._wsReconnectTimer) {
        clearTimeout(self._wsReconnectTimer)
        self._wsReconnectTimer = null
      }

      // Build full WebSocket URL with token query param
      let wsBase = wsUrl.startsWith('ws') ? wsUrl : window.location.origin.replace('http', 'ws') + wsUrl
      let url = `${wsBase}?token=${token}`
      console.log('ws url: ', url)

      let ws
      try {
        ws = new WebSocket(url)
      } catch(error) {
        self.websocket = null
        self._scheduleWsReconnect()
        return
      }
      self.websocket = ws

      // Re-attach listeners of open views (channel-mixin) to the new socket
      for (const fn of self._messageListeners)
        ws.addEventListener('message', fn)

      self.websocket.onopen = () => {
        if (self.websocket !== ws) return
        console.log('WebSocket connection established.');
        self.connected = true;
        self._wsReconnectAttempts = 0
        // No authentication_challenge needed - token is in the URL
        if (self._wsHadConnection) {
          // Reconnect: resync unreads/contacts missed while disconnected.
          // The consumer re-subscribed us to all our channels on connect.
          self._lastUnreadsFetch = 0
          self.getDirectUnreads()
        }
        self._wsHadConnection = true
      };

      self.websocket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        if (message.event === 'subscribed') {
          // Server added us to a (new) channel group, e.g. a fresh direct
          // channel created by the counterpart - refresh the channel list.
          self.fetchContactsForUser()
          return
        }
        if (message.event === 'posted') {
          let data = message.data
          // Skip own messages — the post is already shown via createPost()
          if (data.user_id === self.getUserUuid()) return
          if (data.channel_id) {
            let channel = self.observed.directChannels.find(c => c.id === data.channel_id)
            if (channel) {
              Vue.set(channel, 'numUnreadMessages', (channel.numUnreadMessages || 0) + 1)
              self.observed.numDirectUnreadMessages += 1
              self.observed.directMessageCounter += 1
            } else if (data.channel_type === 'D') {
              // Unknown direct channel - refetch to pick it up incl. unreads
              self.fetchContactsForUser()
            }
          }
        }
      }

      self.websocket.onclose = () => {
        console.log('WebSocket connection closed.');
        // A late close of an already replaced socket must not touch the
        // current connection state.
        if (self.websocket !== ws) return
        self.connected = false;
        self.websocket = null
        self._scheduleWsReconnect()
      };
    }

    self.disConnectWebSocket = function() {
      self._wsShouldReconnect = false
      if (self._wsReconnectTimer) {
        clearTimeout(self._wsReconnectTimer)
        self._wsReconnectTimer = null
      }
      if (self.websocket) {
        self.websocket.close()
        self.websocket = null
      }
    }

    self.initChat = function() {
      if (navigator.onLine && self.getToken()) {
        self.connectWebSocket()
        self.getDirectUnreads()
      }
    }

    self._setupConnectivityListeners = function() {
      if (self._connectivityListenersSet) return
      self._connectivityListenersSet = true
      window.addEventListener('online', () => {
        self.ensureWebSocket()
        self.getDirectUnreads()
      })
      document.addEventListener('visibilitychange', () => {
        // App returns from background (mobile standby kills the socket)
        if (document.hidden) return
        self.ensureWebSocket()
        self.getDirectUnreads()
        self.checkAppVersion()
      })
    }

    self.performLogout = function() {
      localStorage.removeItem('speaker_tool_token')
      localStorage.removeItem('speaker_tool_user')
      localStorage.removeItem('user_uuid')
      localStorage.removeItem('currentEventShortName')
      self.disConnectWebSocket()
      self.observed.directChannels = []
      self.observed.contactIds = []
      self.observed.numDirectUnreadMessages = 0
      self.observed.currentEventShortName = ''
    }

    self.performLogin = function(data) {
      localStorage.setItem('speaker_tool_token', data.token)
      if (data.user_uuid)
        localStorage.setItem('user_uuid', data.user_uuid)
      // Clear stale chat cache so contacts and unreads are fetched fresh
      localStorage.removeItem('direct-channels')
      localStorage.removeItem('contact-ids')
      self.observed.directChannels = []
      self.observed.contactIds = []
      self.observed.numDirectUnreadMessages = 0
      self._lastUnreadsFetch = 0
      self.disConnectWebSocket()
      self.initChat()
    }

    self.askForPushNotificationPermission = function() {
      if ('Notification' in window && 'serviceWorker' in navigator) {
        if (Notification.permission === 'default') {
          // default means, not granted and not denied, no ask so far.
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              self.subscribeUserToPush()
            } else {
              alert('You will not be able to receive private messages from community members, when app is inactive. You can change this via System settings notifications.')
              self.set('pushNotification', 'denied')
            }
          })
        } else if (Notification.permission === 'granted') {
          // multiple calls will work
          self.subscribeUserToPush()
        }
      }
    }

    self.subscribeUserToPush = function() {
      navigator.serviceWorker.ready.then(function(registration) {
        const applicationServerKey = api.defaults.pushApplicationServerKey
        registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        }).then(function(subscription) {
          console.log('User is subscribed:', subscription);
          // send subscription to server
          const payload = {
            endpoint: subscription.endpoint,
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          }
          api.post(`zpcard/subscribe-push-notifications/`, payload).then(response => {
            console.log(response)
            self.set('pushNotification', 'subscribed')
          })
        }).catch(function(error) {
          console.error('Failed to subscribe the user:', error);
        });
      })
    }

    self.appVersion = process.env.APP_VERSION
    self._lastVersionCheck = 0

    self.checkAppVersion = function() {
      if (!navigator.onLine) return
      let now = Date.now()
      // Throttle: max once per 2 minutes
      if (now - self._lastVersionCheck < 120000) return
      self._lastVersionCheck = now

      api.get('zpcard/version/').then(response => {
        let serverVersion = response.data.app_version
        if (serverVersion && serverVersion !== self.appVersion) {
          // Guard against reload loop: only reload once per version
          let reloadedKey = 'app_version_reloaded'
          if (sessionStorage.getItem(reloadedKey) === serverVersion) return
          // Don't reload while the user is typing (e.g. profile form) -
          // the next navigation/visibility check will retry.
          let el = document.activeElement
          if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return
          sessionStorage.setItem(reloadedKey, serverVersion)
          console.log(`App update: ${self.appVersion} -> ${serverVersion}, reloading.`)
          window.location.reload()
        }
      }).catch(() => {})  // silently ignore fetch errors
    }

    self.initChat()
    self._setupConnectivityListeners()

    // remove the old ids from localStorage which have an length of 13
    // ["ed058d770df84","f4606dc2b3464","0bcd7af07fba4"]
    for (const key of ['favorite_exhibitor', 'favorite_sponsor']) {
      let favs = self.getItem(key, [])
      for (const fav of favs) {
        if (fav.length <= 13) {  // alte id
          localStorage.removeItem(key)
          localStorage.removeItem(`${key.substring(9)}s`)
          break
        }
      }
    }

    // rename the old speaker_tool_marked key in localStorage to favorite_session
    let favSessions = self.getItem('speaker_tool_marked', [])
    if (favSessions.length) {
      self.setItem('favorite_session', favSessions)
      localStorage.removeItem('speaker_tool_marked')
    }
}
